/**
 * Native order tracking — WhatsApp OTP, then live Delhivery scans.
 *
 * Replaces the third-party tracking page. Two deliberate differences from how
 * those usually work:
 *
 * 1. No order number. The buyer verifies their WhatsApp number and sees every
 *    order on it. The usual "order number + email" form lets anyone who guesses
 *    a valid pair read a stranger's order; an OTP can't be guessed.
 * 2. Live scans come from Delhivery via the store's own Cloudflare Worker, not
 *    from the courier link on the fulfillment — 17TRACK rewrote that field to
 *    point at its own proxy, so it isn't a link we control or trust.
 *
 * Order data comes from the app proxy, which re-checks the OTP session against
 * the phone being asked about before returning anything.
 */

const API_BASE = '/apps/precheckout';
const OTP_LENGTH = 6;

/*
 * The OTP session outlives the page. The backend token stays valid for its
 * configured TTL, so asking the buyer to re-verify on every reload — or when
 * they come back an hour later to check the same parcel — is pure friction.
 * The token is remembered locally and orders are re-fetched with it; a 401
 * (expired or revoked) falls back to the phone step cleanly. localStorage on
 * the buyer's own device carries the same trust as the WhatsApp inbox the OTP
 * arrived in.
 */
const SESSION_KEY = 'vk_track_session';

function readSavedSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const saved = JSON.parse(raw);
    return saved && saved.token && saved.phone ? saved : null;
  } catch {
    return null;
  }
}

function writeSavedSession(session) {
  try {
    if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session));
    else localStorage.removeItem(SESSION_KEY);
  } catch {
    /* storage may be unavailable (private mode) — the flow still works, it
       just asks for the OTP again next time */
  }
}

async function fetchJson(url, options = {}) {
  const response = await fetch(url, {
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    ...options,
  });
  let payload = null;
  try {
    payload = await response.json();
  } catch {
    payload = null;
  }
  if (!response.ok) throw new Error(payload?.message || `Request failed (${response.status}).`);
  return payload || {};
}

function setBusy(button, isBusy, busyLabel) {
  if (!button) return;
  const label = button.querySelector('.button-text') || button;
  if (isBusy) {
    if (!button.dataset.idleLabel) button.dataset.idleLabel = label.textContent;
    if (busyLabel) label.textContent = busyLabel;
    button.disabled = true;
    return;
  }
  if (button.dataset.idleLabel) label.textContent = button.dataset.idleLabel;
  button.disabled = false;
}

function setMessage(el, text, tone = 'info') {
  if (!el) return;
  el.textContent = text || '';
  el.dataset.tone = text ? tone : '';
  el.hidden = !text;
}

function formatDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
}

/**
 * Shopify's own status is the honest fallback. A parcel only has courier scans
 * once it has actually been handed over, so an unfulfilled order should say
 * "being packed", not sit there looking broken with an empty timeline.
 */
function readableStatus(order) {
  if (order.cancelled_at) return 'Cancelled';
  switch (order.fulfillment_status) {
    case 'FULFILLED':
      return 'Shipped';
    case 'PARTIALLY_FULFILLED':
      return 'Partly shipped';
    case 'UNFULFILLED':
      return 'Being packed';
    default:
      return order.fulfillment_status || '';
  }
}

const RETURN_STATUS_LABELS = {
  RETURN_REQUESTED: 'Return requested',
  IN_PROGRESS: 'Return in progress',
  INSPECTION_COMPLETE: 'Return inspected',
  RETURNED: 'Returned',
  RETURN_FAILED: 'Return failed',
};

function init() {
  const root = document.querySelector('[data-order-tracking]');
  if (!root) return;

  const trackingApi = (root.dataset.trackingApi || '').replace(/\/$/, '');
  const message = root.querySelector('[data-message]');
  const steps = Array.from(root.querySelectorAll('[data-step]'));
  const otpInputs = Array.from(root.querySelectorAll('[data-otp-digit]'));
  const results = root.querySelector('[data-results]');

  const state = { phone: '', token: '' };

  function goToStep(name) {
    steps.forEach((step) => {
      step.hidden = step.dataset.step !== name;
    });
  }

  /* --- OTP boxes: a code pasted whole lands across all six ---------------- */

  function otpValue() {
    return otpInputs.map((input) => input.value.trim()).join('');
  }

  function fillOtp(digits) {
    const chars = String(digits).replace(/\D/g, '').slice(0, OTP_LENGTH).split('');
    otpInputs.forEach((input, i) => {
      input.value = chars[i] || '';
    });
    const nextEmpty = otpInputs.find((input) => !input.value);
    (nextEmpty || otpInputs[otpInputs.length - 1]).focus();
    return chars.length === OTP_LENGTH;
  }

  otpInputs.forEach((input, index) => {
    input.addEventListener('input', () => {
      if (input.value.length > 1) {
        if (fillOtp(input.value)) verifyOtp();
        return;
      }
      if (input.value && index < otpInputs.length - 1) otpInputs[index + 1].focus();
      if (otpValue().length === OTP_LENGTH) verifyOtp();
    });
    input.addEventListener('keydown', (event) => {
      if (event.key === 'Backspace' && !input.value && index > 0) otpInputs[index - 1].focus();
    });
    input.addEventListener('paste', (event) => {
      event.preventDefault();
      const pasted = (event.clipboardData || window.clipboardData).getData('text');
      if (fillOtp(pasted)) verifyOtp();
    });
  });

  /* --- rendering ---------------------------------------------------------- */

  function renderOrders(orders) {
    results.innerHTML = '';

    if (!orders.length) {
      const empty = document.createElement('p');
      empty.className = 'order-tracking__empty';
      empty.textContent = 'No orders found on this number yet.';
      results.appendChild(empty);
      return;
    }

    orders.forEach((order, orderIndex) => {
      const card = document.createElement('article');
      card.className = 'order-tracking__order';

      const head = document.createElement('div');
      head.className = 'order-tracking__order-head';
      const name = document.createElement('span');
      name.className = 'order-tracking__order-name';
      name.textContent = order.name;
      const status = document.createElement('span');
      status.className = 'order-tracking__badge';
      status.dataset.status = readableStatus(order).toLowerCase().replace(/\s+/g, '-');
      status.textContent = readableStatus(order);
      const placed = document.createElement('span');
      placed.className = 'order-tracking__order-date';
      placed.textContent = formatDate(order.placed_at);
      head.append(name, status, placed);
      card.appendChild(head);

      order.items.forEach((item) => {
        const line = document.createElement('div');
        line.className = 'order-tracking__item';
        if (item.image) {
          const img = document.createElement('img');
          // Ask the CDN for a thumbnail-sized rendition rather than pulling the
          // full product image just to show it at 48px.
          const separator = item.image.includes('?') ? '&' : '?';
          img.src = `${item.image}${separator}width=96`;
          img.alt = '';
          img.loading = 'lazy';
          img.width = 48;
          img.height = 64;
          line.appendChild(img);
        }
        const title = document.createElement('span');
        title.textContent = `${item.title}${item.quantity > 1 ? ` × ${item.quantity}` : ''}`;
        line.appendChild(title);
        card.appendChild(line);
      });

      if (order.shipments.length) {
        order.shipments.forEach((shipment) => {
          const box = document.createElement('div');
          box.className = 'order-tracking__shipment';
          box.dataset.waybill = shipment.waybill;

          const head = document.createElement('div');
          head.className = 'order-tracking__awb';
          const courier = document.createElement('span');
          courier.textContent = shipment.company || 'Courier';
          const awb = document.createElement('code');
          awb.textContent = shipment.waybill;
          head.append(courier, awb);
          box.appendChild(head);
          card.appendChild(box);

          /*
           * Only the newest order fetches its scans up front. A long-standing
           * buyer here has 62 orders, and asking the courier about every one
           * on page load would mean sixty-odd requests for a timeline nobody
           * asked to see. The rest load on tap — which is also how the buyer
           * actually uses this: they come to check one parcel.
           */
          if (orderIndex === 0) {
            const loading = document.createElement('p');
            loading.className = 'order-tracking__loading';
            loading.textContent = 'Fetching latest status…';
            box.appendChild(loading);
            loadScans(box, shipment.waybill);
            return;
          }

          const trackButton = document.createElement('button');
          trackButton.type = 'button';
          trackButton.className = 'order-tracking__track-button';
          trackButton.textContent = 'Track this parcel';
          trackButton.addEventListener(
            'click',
            () => {
              trackButton.replaceWith(
                Object.assign(document.createElement('p'), {
                  className: 'order-tracking__loading',
                  textContent: 'Fetching latest status…',
                })
              );
              loadScans(box, shipment.waybill);
            },
            { once: true }
          );
          box.appendChild(trackButton);
        });
      } else if (!order.cancelled_at) {
        const note = document.createElement('p');
        note.className = 'order-tracking__note';
        note.textContent = 'Tracking details appear here once your order is handed to the courier.';
        card.appendChild(note);
      }

      /*
       * Returns. A shipped order with no return yet gets a "Return items"
       * button; the request itself lands in Shopify's own Returns workflow, so
       * approval and refund happen in the store admin, not a second system.
       * An order already in a return shows that state as a second badge.
       */
      if (!order.cancelled_at) {
        const returnLabel = RETURN_STATUS_LABELS[order.return_status];
        if (returnLabel) {
          const badge = document.createElement('span');
          badge.className = 'order-tracking__badge order-tracking__badge--return';
          badge.textContent = returnLabel;
          card.querySelector('.order-tracking__order-head').insertBefore(
            badge,
            card.querySelector('.order-tracking__order-date')
          );
        } else if (order.fulfillment_status === 'FULFILLED' && order.id) {
          const returnButton = document.createElement('button');
          returnButton.type = 'button';
          returnButton.className = 'order-tracking__track-button';
          returnButton.textContent = 'Return items';
          returnButton.addEventListener('click', () => openReturnFlow(card, order, returnButton), { once: true });
          card.appendChild(returnButton);
        }
      }

      results.appendChild(card);
    });
  }

  /* --- returns ------------------------------------------------------------ */

  async function openReturnFlow(card, order, trigger) {
    const holder = document.createElement('div');
    holder.className = 'order-tracking__return';
    const loading = document.createElement('p');
    loading.className = 'order-tracking__loading';
    loading.textContent = 'Checking what can be returned…';
    holder.appendChild(loading);
    trigger.replaceWith(holder);

    let eligible;
    try {
      eligible = await fetchJson(`${API_BASE}/returns/eligible`, {
        method: 'POST',
        body: JSON.stringify({ session_token: state.token, buyer_phone: state.phone, order_id: order.id }),
      });
    } catch (error) {
      loading.textContent = error.message;
      return;
    }

    if (!(eligible.items || []).length) {
      // Two different reasons for an empty list, and the buyer deserves the
      // real one: the policy window has closed, or it hasn't shipped/arrived.
      const closed = (eligible.expired_items || [])[0];
      loading.textContent = closed
        ? `The ${eligible.policy.window_days}-day return window for this order closed on ${formatDate(closed.window_closed_at)}.`
        : 'Returns open once this order is delivered. If it has already arrived, please check again in a few hours.';
      return;
    }

    loading.remove();
    holder.appendChild(buildReturnForm(order, eligible, holder, card));
  }

  /**
   * Sends the files straight to Shopify's staged upload target, never through
   * our own backend — a return video can be tens of megabytes and the function
   * body limit is 1MB. The backend only ever sees the resulting resource URLs.
   */
  async function uploadProof(order, files, submitButton) {
    const staged = await fetchJson(`${API_BASE}/returns/upload-url`, {
      method: 'POST',
      body: JSON.stringify({
        session_token: state.token,
        buyer_phone: state.phone,
        order_id: order.id,
        files: files.map((file) => ({ name: file.name, type: file.type, size: file.size })),
      }),
    });

    const uploads = staged.uploads || [];
    if (uploads.length !== files.length) throw new Error('Upload could not be prepared. Please try again.');

    const resourceUrls = [];
    for (let i = 0; i < files.length; i++) {
      setBusy(submitButton, true, `Uploading ${i + 1} of ${files.length}…`);

      // Staged targets take a multipart POST with Shopify's own signed fields;
      // the file must be appended last or the target rejects it.
      const form = new FormData();
      (uploads[i].parameters || []).forEach((parameter) => form.append(parameter.name, parameter.value));
      form.append('file', files[i]);

      const response = await fetch(uploads[i].url, { method: 'POST', body: form });
      if (!response.ok) throw new Error('Upload failed. Please check your connection and try again.');

      resourceUrls.push(uploads[i].resource_url);
    }

    return resourceUrls;
  }

  function buildReturnForm(order, eligible, holder, card) {
    const form = document.createElement('form');
    form.className = 'order-tracking__return-form';
    form.noValidate = true;

    const heading = document.createElement('p');
    heading.className = 'order-tracking__return-heading';
    heading.textContent = 'Select the items to return';
    form.appendChild(heading);

    eligible.items.forEach((item, index) => {
      const row = document.createElement('label');
      row.className = 'order-tracking__return-item';

      const check = document.createElement('input');
      check.type = 'checkbox';
      check.value = item.fulfillment_line_item_id;
      // One returnable item: pre-tick it, the common case needs zero extra taps.
      check.checked = eligible.items.length === 1;
      check.dataset.returnCheck = '';

      const title = document.createElement('span');
      title.className = 'order-tracking__return-title';
      title.textContent = item.title;

      row.append(check, title);

      if (item.quantity > 1) {
        const qty = document.createElement('select');
        qty.dataset.returnQty = item.fulfillment_line_item_id;
        for (let i = 1; i <= item.quantity; i++) {
          const option = document.createElement('option');
          option.value = String(i);
          option.textContent = `Qty ${i}`;
          qty.appendChild(option);
        }
        row.appendChild(qty);
      }

      form.appendChild(row);
    });

    const reasonLabel = document.createElement('label');
    reasonLabel.className = 'order-tracking__field';
    const reasonText = document.createElement('span');
    reasonText.textContent = 'Reason';
    const reasonSelect = document.createElement('select');
    reasonSelect.className = 'order-tracking__input';
    reasonSelect.dataset.returnReason = '';
    eligible.reasons.forEach((reason) => {
      const option = document.createElement('option');
      option.value = reason.id;
      option.textContent = reason.label;
      reasonSelect.appendChild(option);
    });
    reasonLabel.append(reasonText, reasonSelect);
    form.appendChild(reasonLabel);

    const noteLabel = document.createElement('label');
    noteLabel.className = 'order-tracking__field';
    const noteText = document.createElement('span');
    noteText.textContent = 'Anything we should know? (optional)';
    const note = document.createElement('textarea');
    note.className = 'order-tracking__input';
    note.rows = 2;
    note.maxLength = 300;
    note.dataset.returnNote = '';
    noteLabel.append(noteText, note);
    form.appendChild(noteLabel);

    /*
     * Proof is mandatory, so it is asked for plainly rather than buried as an
     * optional extra. The server re-checks it regardless — this is the polite
     * version of a rule that is enforced either way.
     */
    const policy = eligible.policy || {};
    let proofInput = null;
    let proofList = null;

    if (policy.proof_required) {
      const proofLabel = document.createElement('label');
      proofLabel.className = 'order-tracking__field';
      const proofText = document.createElement('span');
      proofText.textContent = `Photo or video of the item (required, up to ${policy.max_files || 5})`;
      proofInput = document.createElement('input');
      proofInput.type = 'file';
      proofInput.className = 'order-tracking__input';
      proofInput.accept = 'image/*,video/*';
      proofInput.multiple = (policy.max_files || 5) > 1;
      proofLabel.append(proofText, proofInput);
      form.appendChild(proofLabel);

      proofList = document.createElement('p');
      proofList.className = 'order-tracking__note';
      form.appendChild(proofList);

      const maxBytes = policy.max_bytes || 50 * 1024 * 1024;
      proofInput.addEventListener('change', () => {
        const chosen = Array.from(proofInput.files || []);
        const oversized = chosen.find((file) => file.size > maxBytes);
        if (oversized) {
          // Caught here as well as server-side, so the buyer isn't left
          // watching a doomed upload of a 200MB video crawl to a rejection.
          proofList.textContent = `"${oversized.name}" is larger than ${Math.round(maxBytes / (1024 * 1024))} MB. Please choose a smaller file.`;
          proofInput.value = '';
          return;
        }
        proofList.textContent = chosen.length
          ? `${chosen.length} file${chosen.length > 1 ? 's' : ''} selected.`
          : '';
      });
    }

    const submit = document.createElement('button');
    submit.type = 'submit';
    submit.className = 'order-tracking__submit button';
    submit.innerHTML = '<span class="button-text">Request return</span>';
    form.appendChild(submit);

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const lineItems = Array.from(form.querySelectorAll('[data-return-check]:checked')).map((check) => ({
        fulfillment_line_item_id: check.value,
        quantity: parseInt(form.querySelector(`[data-return-qty="${check.value}"]`)?.value, 10) || 1,
        reason_id: reasonSelect.value,
        note: note.value.trim(),
      }));

      if (!lineItems.length) {
        setMessage(message, 'Select at least one item to return.', 'warning');
        return;
      }

      const chosenFiles = proofInput ? Array.from(proofInput.files || []) : [];
      if (policy.proof_required && chosenFiles.length < (policy.min_files || 1)) {
        setMessage(message, 'Please attach a photo or video of the item.', 'warning');
        return;
      }

      try {
        setBusy(submit, true, 'Sending…');
        setMessage(message, '');

        const proof = chosenFiles.length ? await uploadProof(order, chosenFiles, submit) : [];

        setBusy(submit, true, 'Sending…');
        await fetchJson(`${API_BASE}/returns`, {
          method: 'POST',
          body: JSON.stringify({
            session_token: state.token,
            buyer_phone: state.phone,
            order_id: order.id,
            line_items: lineItems,
            proof,
          }),
        });

        const done = document.createElement('p');
        done.className = 'order-tracking__return-done';
        done.textContent = 'Return requested. We will review it and confirm on WhatsApp.';
        holder.replaceChildren(done);

        const badge = document.createElement('span');
        badge.className = 'order-tracking__badge order-tracking__badge--return';
        badge.textContent = 'Return requested';
        card.querySelector('.order-tracking__order-head').insertBefore(
          badge,
          card.querySelector('.order-tracking__order-date')
        );
      } catch (error) {
        setBusy(submit, false);
        setMessage(message, error.message, 'error');
      }
    });

    return form;
  }

  async function loadScans(box, waybill) {
    if (!trackingApi) {
      box.querySelector('.order-tracking__loading').textContent = 'Live tracking is not configured.';
      return;
    }

    try {
      const data = await fetchJson(`${trackingApi}/track?waybill=${encodeURIComponent(waybill)}`);
      const loading = box.querySelector('.order-tracking__loading');

      if (!data.found || !(data.scans || []).length) {
        loading.textContent = 'The courier has not scanned this parcel yet.';
        return;
      }

      loading.remove();

      if (data.promisedDate) {
        const promised = document.createElement('p');
        promised.className = 'order-tracking__promised';
        promised.textContent = `Expected by ${formatDate(data.promisedDate)}`;
        box.appendChild(promised);
      }

      const timeline = document.createElement('ol');
      timeline.className = 'order-tracking__timeline';
      data.scans.forEach((scan, index) => {
        const item = document.createElement('li');
        if (index === 0) item.className = 'is-current';
        const label = document.createElement('span');
        label.className = 'order-tracking__scan-status';
        label.textContent = scan.detail || scan.status;
        const meta = document.createElement('span');
        meta.className = 'order-tracking__scan-meta';
        meta.textContent = [scan.location, formatDateTime(scan.at)].filter(Boolean).join(' · ');
        item.append(label, meta);
        timeline.appendChild(item);
      });
      box.appendChild(timeline);
    } catch {
      const loading = box.querySelector('.order-tracking__loading');
      if (loading) loading.textContent = 'Could not reach the courier just now. Please try again shortly.';
    }
  }

  /* --- steps -------------------------------------------------------------- */

  const phoneForm = root.querySelector('[data-phone-form]');
  const otpForm = root.querySelector('[data-otp-form]');
  let verifying = false;

  phoneForm?.addEventListener('submit', async (event) => {
    event.preventDefault();
    const button = phoneForm.querySelector('button[type="submit"]');
    const local = (root.querySelector('[data-phone-input]')?.value || '').replace(/\D/g, '');

    if (!/^\d{10}$/.test(local)) {
      setMessage(message, 'Enter a valid 10-digit WhatsApp number.', 'warning');
      return;
    }
    state.phone = `+91${local}`;

    try {
      setBusy(button, true, 'Sending…');
      setMessage(message, '');
      await fetchJson(`${API_BASE}/auth/send-otp`, {
        method: 'POST',
        body: JSON.stringify({ phone: state.phone, source: 'order-tracking' }),
      });
      goToStep('otp');
      setMessage(message, 'OTP sent on WhatsApp.', 'success');
      otpInputs[0]?.focus();
    } catch (error) {
      setMessage(message, error.message, 'error');
    } finally {
      setBusy(button, false);
    }
  });

  async function verifyOtp() {
    if (verifying) return;
    const otp = otpValue();
    if (otp.length !== OTP_LENGTH) return;

    const button = otpForm?.querySelector('button[type="submit"]');
    verifying = true;

    try {
      setBusy(button, true, 'Verifying…');
      setMessage(message, '');
      const auth = await fetchJson(`${API_BASE}/auth/verify-otp`, {
        method: 'POST',
        body: JSON.stringify({ phone: state.phone, otp }),
      });
      state.token = auth.session_token || '';
      state.phone = auth.buyer?.phone || state.phone;

      setBusy(button, true, 'Loading orders…');
      await loadOrders();
      writeSavedSession({ token: state.token, phone: state.phone });
      setMessage(message, '');
    } catch (error) {
      fillOtp('');
      otpInputs[0]?.focus();
      setMessage(message, error.message, 'error');
    } finally {
      verifying = false;
      setBusy(button, false);
    }
  }

  async function loadOrders() {
    const data = await fetchJson(`${API_BASE}/orders`, {
      method: 'POST',
      body: JSON.stringify({ session_token: state.token, buyer_phone: state.phone }),
    });
    renderOrders(data.orders || []);
    goToStep('results');
  }

  otpForm?.addEventListener('submit', (event) => {
    event.preventDefault();
    verifyOtp();
  });

  // querySelectorAll, not querySelector: this control exists twice — on the
  // OTP step and on the results step — and binding only the first left the
  // results-page "Check a different number" doing nothing at all.
  root.querySelectorAll('[data-change-number]').forEach((button) => {
    button.addEventListener('click', () => {
      writeSavedSession(null); // a different number means this session is done
      state.token = '';
      state.phone = '';
      fillOtp('');
      goToStep('phone');
      setMessage(message, '');
      root.querySelector('[data-phone-input]')?.focus();
    });
  });

  /*
   * Reload: pick up where the buyer left off. A saved session skips straight
   * to the order list; if the backend says it has expired, fall back to the
   * phone step without an error banner — being asked to verify again after a
   * long gap is expected, not a failure.
   */
  const saved = readSavedSession();
  if (saved) {
    state.token = saved.token;
    state.phone = saved.phone;
    goToStep('phone');
    setMessage(message, 'Loading your orders…', 'info');
    loadOrders()
      .then(() => setMessage(message, ''))
      .catch(() => {
        writeSavedSession(null);
        state.token = '';
        state.phone = '';
        goToStep('phone');
        setMessage(message, '');
      });
  } else {
    goToStep('phone');
  }
}

init();
